import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import dataService from '../services/DataService';
import ErrorHandler from '../utils/ErrorHandler';

/**
 * Data Store
 * Manages all application data (projects, contacts, statistics)
 */
export const useDataStore = create(
  subscribeWithSelector((set, get) => ({
    // State
    projects: [],
    ongoingProjectsList: [], // Simplified list for employee assignment
    contacts: [],
    clients: [],
    employees: [],
    freeEmployees: [],
    stats: {
      totalProjects: 0,
      ongoingProjects: 0,
      completedProjects: 0,
      pendingContacts: 0,
      totalEmployees: 0,
      freeEmployees: 0
    },
    
    // Loading states
    isLoading: false,
    isRefreshing: false,
    lastRefresh: null,
    error: null,
    
    // Cache management
    dataLoaded: false,
    
    // Actions
    loadAllData: async () => {
      const state = get();
      
      // Prevent multiple simultaneous loads
      if (state.isLoading || state.isRefreshing) return;
      
      set({ isLoading: true, error: null });

      try {
        // Load data in parallel for better performance
        const [dashboardData, projectsData, contactsData, clientsData, employeesData, freeEmployeesData, ongoingProjectsListData] = await Promise.allSettled([
          dataService.getDashboardCounts(),
          dataService.getAllProjects(),
          dataService.getAllInquiries(),
          dataService.getAllClients(),
          dataService.getAllEmployees(),
          dataService.getFreeEmployees(),
          dataService.getOngoingProjectsList()
        ]);

        const updates = { isLoading: false, dataLoaded: true };

        // Handle dashboard counts
        if (dashboardData.status === 'fulfilled') {
          console.log('[DataStore] Dashboard API response:', dashboardData.value);
          
          // Check if the API response has the expected field names
          const apiStats = dashboardData.value;
          if (apiStats && typeof apiStats === 'object') {
            // Map API response to expected format (handle different field names)
            updates.stats = {
              totalProjects: apiStats.totalProjects || apiStats.total_projects || apiStats.total || 0,
              ongoingProjects: apiStats.ongoingProjects || apiStats.ongoing_projects || apiStats.ongoing || 0,
              completedProjects: apiStats.completedProjects || apiStats.completed_projects || apiStats.completed || 0,
              pendingContacts: apiStats.pendingContacts || apiStats.pending_contacts || apiStats.pending_inquiries || apiStats.pending || 0,
              totalEmployees: apiStats.totalEmployees || apiStats.total_employees || (employeesData.status === 'fulfilled' ? (employeesData.value || []).length : 0),
              freeEmployees: apiStats.freeEmployees || apiStats.free_employees || (freeEmployeesData.status === 'fulfilled' ? (freeEmployeesData.value || []).length : 0)
            };
          } else {
            updates.stats = apiStats;
          }
        } else {
          console.error('Failed to load dashboard counts:', dashboardData.reason);
          // Fallback: calculate stats from projects and contacts data
          if (projectsData.status === 'fulfilled' && contactsData.status === 'fulfilled') {
            const projects = projectsData.value || [];
            const contacts = contactsData.value || [];
            const employees = employeesData.status === 'fulfilled' ? (employeesData.value || []) : [];
            const freeEmployees = freeEmployeesData.status === 'fulfilled' ? (freeEmployeesData.value || []) : [];
            
            updates.stats = {
              totalProjects: projects.length,
              ongoingProjects: projects.filter(p => p.status === 'ongoing').length,
              completedProjects: projects.filter(p => p.status === 'completed').length,
              pendingContacts: contacts.filter(c => c.status === 'pending').length,
              totalEmployees: employees.length,
              freeEmployees: freeEmployees.length
            };
          }
        }

        // Handle projects data
        if (projectsData.status === 'fulfilled') {
          updates.projects = projectsData.value || [];
        } else {
          console.error('Failed to load projects:', projectsData.reason);
          updates.projects = [];
        }

        // Handle contacts data
        if (contactsData.status === 'fulfilled') {
          updates.contacts = contactsData.value || [];
        } else {
          console.error('Failed to load contacts:', contactsData.reason);
          updates.contacts = [];
        }

        // Handle clients data
        if (clientsData.status === 'fulfilled') {
          updates.clients = clientsData.value || [];
        } else {
          console.error('Failed to load clients:', clientsData.reason);
          updates.clients = [];
        }

        // Handle employees data
        if (employeesData.status === 'fulfilled') {
          updates.employees = employeesData.value || [];
        } else {
          console.error('Failed to load employees:', employeesData.reason);
          updates.employees = [];
        }

        // Handle free employees data
        if (freeEmployeesData.status === 'fulfilled') {
          updates.freeEmployees = freeEmployeesData.value || [];
        } else {
          console.error('Failed to load free employees:', freeEmployeesData.reason);
          updates.freeEmployees = [];
        }

        // Handle ongoing projects list (for employee assignment)
        if (ongoingProjectsListData.status === 'fulfilled') {
          updates.ongoingProjectsList = ongoingProjectsListData.value || [];
        } else {
          console.error('Failed to load ongoing projects list:', ongoingProjectsListData.reason);
          updates.ongoingProjectsList = [];
        }

        // Ensure error is cleared on successful load
        updates.error = null;
        set(updates);

      } catch (error) {
        console.error('Error loading data:', error);
        const errorInfo = ErrorHandler.handleApiError(error, 'Load Admin Data');
        
        set({
          isLoading: false,
          error: errorInfo.message
        });
        
        if (errorInfo.shouldRedirect) {
          // Let auth store handle the redirect
          throw error;
        }
      }
    },

    refreshData: async () => {
      const state = get();
      
      // Prevent multiple simultaneous refreshes
      if (state.isRefreshing || state.isLoading) return;
      
      set({ isRefreshing: true, error: null });
      
      try {
        await get().loadAllData();
        set({ 
          isRefreshing: false,
          lastRefresh: new Date(),
          error: null // Clear any previous errors on successful refresh
        });
      } catch (error) {
        set({ isRefreshing: false });
        throw error;
      }
    },

    // Contact management
    updateContactStatus: async (contactId, newStatus) => {
      const state = get();
      const originalContacts = [...state.contacts];
      
      // Optimistic update
      set({
        contacts: state.contacts.map(contact => 
          contact.id === contactId 
            ? { ...contact, status: newStatus }
            : contact
        )
      });

      try {
        await dataService.updateInquiry(contactId, { status: newStatus });
        
        // Refresh dashboard counts after successful update (non-blocking)
        try {
          const updatedStats = await dataService.getDashboardCounts();
          if (updatedStats && typeof updatedStats === 'object') {
            set({ stats: {
              totalProjects: updatedStats.totalProjects || updatedStats.total_projects || 0,
              ongoingProjects: updatedStats.ongoingProjects || updatedStats.ongoing_projects || 0,
              completedProjects: updatedStats.completedProjects || updatedStats.completed_projects || 0,
              pendingContacts: updatedStats.pendingContacts || updatedStats.pending_contacts || 0,
              totalEmployees: updatedStats.totalEmployees || updatedStats.total_employees || 0,
              freeEmployees: updatedStats.freeEmployees || updatedStats.free_employees || 0
            }});
          } else {
            set({ stats: updatedStats });
          }
        } catch (statsError) {
          console.warn('Failed to refresh dashboard counts after contact update:', statsError);
          // Update pending contacts count manually
          const currentState = get();
          const pendingCount = currentState.contacts.filter(c => c.status === 'pending').length;
          set(state => ({
            stats: {
              ...state.stats,
              pendingContacts: pendingCount
            }
          }));
        }
        
      } catch (error) {
        console.error('Error updating contact status:', error);
        
        // Revert optimistic update on error
        set({ contacts: originalContacts });
        
        const errorInfo = ErrorHandler.handleApiError(error, 'Update Contact Status');
        set({ error: errorInfo.message });
        
        // Clear error after 5 seconds
        setTimeout(() => {
          const currentState = get();
          if (currentState.error === errorInfo.message) {
            set({ error: null });
          }
        }, 5000);
        
        throw error;
      }
    },

    // Project management
    addProject: async (projectData) => {
      try {
        const newProject = await dataService.createProject(projectData);
        
        // Update local state
        set(state => ({
          projects: [...state.projects, newProject]
        }));
        
        // Refresh dashboard counts and ongoing projects list (non-blocking)
        try {
          const [updatedStats, updatedOngoingList] = await Promise.allSettled([
            dataService.getDashboardCounts(),
            dataService.getOngoingProjectsList()
          ]);
          
          if (updatedStats.status === 'fulfilled' && updatedStats.value && typeof updatedStats.value === 'object') {
            set({ stats: {
              totalProjects: updatedStats.value.totalProjects || updatedStats.value.total_projects || 0,
              ongoingProjects: updatedStats.value.ongoingProjects || updatedStats.value.ongoing_projects || 0,
              completedProjects: updatedStats.value.completedProjects || updatedStats.value.completed_projects || 0,
              pendingContacts: updatedStats.value.pendingContacts || updatedStats.value.pending_contacts || 0,
              totalEmployees: updatedStats.value.totalEmployees || updatedStats.value.total_employees || 0,
              freeEmployees: updatedStats.value.freeEmployees || updatedStats.value.free_employees || 0
            }});
          }
          
          if (updatedOngoingList.status === 'fulfilled') {
            set({ ongoingProjectsList: updatedOngoingList.value || [] });
          }
        } catch (statsError) {
          console.warn('Failed to refresh dashboard counts after project creation:', statsError);
          // Update project counts manually
          const currentState = get();
          const projects = currentState.projects;
          set(state => ({
            stats: {
              ...state.stats,
              totalProjects: projects.length,
              ongoingProjects: projects.filter(p => p.status === 'ongoing').length,
              completedProjects: projects.filter(p => p.status === 'completed').length
            }
          }));
        }
        
        return newProject;
      } catch (error) {
        console.error('Error adding project:', error);
        const errorInfo = ErrorHandler.handleApiError(error, 'Add Project');
        set({ error: errorInfo.message });
        throw error;
      }
    },

    updateProject: async (projectId, projectData) => {
      const state = get();
      const originalProjects = [...state.projects];
      
      // Optimistic update
      set({
        projects: state.projects.map(project => 
          project.id === projectId 
            ? { ...project, ...projectData }
            : project
        )
      });

      try {
        const updatedProject = await dataService.updateProject(projectId, projectData);
        
        // Update with server response
        set({
          projects: state.projects.map(project => 
            project.id === projectId ? updatedProject : project
          )
        });
        
        // Refresh dashboard counts and ongoing projects list (non-blocking)
        try {
          const [updatedStats, updatedOngoingList] = await Promise.allSettled([
            dataService.getDashboardCounts(),
            dataService.getOngoingProjectsList()
          ]);
          
          if (updatedStats.status === 'fulfilled' && updatedStats.value && typeof updatedStats.value === 'object') {
            set({ stats: {
              totalProjects: updatedStats.value.totalProjects || updatedStats.value.total_projects || 0,
              ongoingProjects: updatedStats.value.ongoingProjects || updatedStats.value.ongoing_projects || 0,
              completedProjects: updatedStats.value.completedProjects || updatedStats.value.completed_projects || 0,
              pendingContacts: updatedStats.value.pendingContacts || updatedStats.value.pending_contacts || 0,
              totalEmployees: updatedStats.value.totalEmployees || updatedStats.value.total_employees || 0,
              freeEmployees: updatedStats.value.freeEmployees || updatedStats.value.free_employees || 0
            }});
          }
          
          if (updatedOngoingList.status === 'fulfilled') {
            set({ ongoingProjectsList: updatedOngoingList.value || [] });
          }
        } catch (statsError) {
          console.warn('Failed to refresh dashboard counts after project update:', statsError);
        }
        
        return updatedProject;
      } catch (error) {
        console.error('Error updating project:', error);
        
        // Revert optimistic update
        set({ projects: originalProjects });
        
        const errorInfo = ErrorHandler.handleApiError(error, 'Update Project');
        set({ error: errorInfo.message });
        throw error;
      }
    },

    linkProjectClient: async (projectId, clientId) => {
      try {
        const linkedProject = await dataService.linkProjectClient(projectId, clientId);
        
        // Update local state with the linked project
        set(state => ({
          projects: state.projects.map(project => 
            project.id === projectId ? linkedProject : project
          )
        }));
        
        // Refresh dashboard counts and ongoing projects list (non-blocking)
        try {
          const [updatedStats, updatedOngoingList] = await Promise.allSettled([
            dataService.getDashboardCounts(),
            dataService.getOngoingProjectsList()
          ]);
          
          if (updatedStats.status === 'fulfilled' && updatedStats.value && typeof updatedStats.value === 'object') {
            set({ stats: {
              totalProjects: updatedStats.value.totalProjects || updatedStats.value.total_projects || 0,
              ongoingProjects: updatedStats.value.ongoingProjects || updatedStats.value.ongoing_projects || 0,
              completedProjects: updatedStats.value.completedProjects || updatedStats.value.completed_projects || 0,
              pendingContacts: updatedStats.value.pendingContacts || updatedStats.value.pending_contacts || 0,
              totalEmployees: updatedStats.value.totalEmployees || updatedStats.value.total_employees || 0,
              freeEmployees: updatedStats.value.freeEmployees || updatedStats.value.free_employees || 0
            }});
          }
          
          if (updatedOngoingList.status === 'fulfilled') {
            set({ ongoingProjectsList: updatedOngoingList.value || [] });
          }
        } catch (statsError) {
          console.warn('Failed to refresh dashboard counts after linking project with client:', statsError);
        }
        
        return linkedProject;
      } catch (error) {
        console.error('Error linking project with client:', error);
        const errorInfo = ErrorHandler.handleApiError(error, 'Link Project with Client');
        set({ error: errorInfo.message });
        throw error;
      }
    },

    deleteProject: async (projectId) => {
      const state = get();
      const originalProjects = [...state.projects];
      
      // Optimistic update
      set({
        projects: state.projects.filter(project => project.id !== projectId)
      });

      try {
        await dataService.deleteProject(projectId);
        
        // Refresh dashboard counts and ongoing projects list (non-blocking)
        try {
          const [updatedStats, updatedOngoingList] = await Promise.allSettled([
            dataService.getDashboardCounts(),
            dataService.getOngoingProjectsList()
          ]);
          
          if (updatedStats.status === 'fulfilled' && updatedStats.value && typeof updatedStats.value === 'object') {
            set({ stats: {
              totalProjects: updatedStats.value.totalProjects || updatedStats.value.total_projects || 0,
              ongoingProjects: updatedStats.value.ongoingProjects || updatedStats.value.ongoing_projects || 0,
              completedProjects: updatedStats.value.completedProjects || updatedStats.value.completed_projects || 0,
              pendingContacts: updatedStats.value.pendingContacts || updatedStats.value.pending_contacts || 0,
              totalEmployees: updatedStats.value.totalEmployees || updatedStats.value.total_employees || 0,
              freeEmployees: updatedStats.value.freeEmployees || updatedStats.value.free_employees || 0
            }});
          }
          
          if (updatedOngoingList.status === 'fulfilled') {
            set({ ongoingProjectsList: updatedOngoingList.value || [] });
          }
        } catch (statsError) {
          console.warn('Failed to refresh dashboard counts after project deletion:', statsError);
          // Update project counts manually
          const currentState = get();
          set(state => ({
            stats: {
              ...state.stats,
              totalProjects: currentState.projects.length,
              ongoingProjects: currentState.projects.filter(p => p.status === 'ongoing').length,
              completedProjects: currentState.projects.filter(p => p.status === 'completed').length
            }
          }));
        }
        
      } catch (error) {
        console.error('Error deleting project:', error);
        
        // Revert optimistic update
        set({ projects: originalProjects });
        
        const errorInfo = ErrorHandler.handleApiError(error, 'Delete Project');
        set({ error: errorInfo.message });
        throw error;
      }
    },

    // Employee management
    addEmployee: async (employeeData) => {
      try {
        const newEmployee = await dataService.createEmployee(employeeData);
        
        // Update local state
        set(state => ({
          employees: [...state.employees, newEmployee]
        }));
        
        // Refresh dashboard counts (non-blocking - don't fail if this errors)
        try {
          const updatedStats = await dataService.getDashboardCounts();
          if (updatedStats && typeof updatedStats === 'object') {
            set({ stats: {
              totalProjects: updatedStats.totalProjects || updatedStats.total_projects || 0,
              ongoingProjects: updatedStats.ongoingProjects || updatedStats.ongoing_projects || 0,
              completedProjects: updatedStats.completedProjects || updatedStats.completed_projects || 0,
              pendingContacts: updatedStats.pendingContacts || updatedStats.pending_contacts || 0,
              totalEmployees: updatedStats.totalEmployees || updatedStats.total_employees || 0,
              freeEmployees: updatedStats.freeEmployees || updatedStats.free_employees || 0
            }});
          } else {
            set({ stats: updatedStats });
          }
        } catch (statsError) {
          // Log but don't fail the employee creation if stats refresh fails
          console.warn('Failed to refresh dashboard counts after employee creation:', statsError);
          // Update employee count manually since we know we added one
          set(state => ({
            stats: {
              ...state.stats,
              totalEmployees: (state.stats.totalEmployees || 0) + 1,
              freeEmployees: newEmployee.status === 'free' 
                ? (state.stats.freeEmployees || 0) + 1 
                : state.stats.freeEmployees || 0
            }
          }));
        }
        
        return newEmployee;
      } catch (error) {
        console.error('Error adding employee:', error);
        const errorInfo = ErrorHandler.handleApiError(error, 'Add Employee');
        set({ error: errorInfo.message });
        throw error;
      }
    },

    updateEmployee: async (employeeId, employeeData) => {
      const state = get();
      const originalEmployees = [...state.employees];
      
      // Optimistic update
      set({
        employees: state.employees.map(employee => 
          employee.id === employeeId 
            ? { ...employee, ...employeeData }
            : employee
        )
      });

      try {
        const updatedEmployee = await dataService.updateEmployee(employeeId, employeeData);
        
        // Update with server response
        set({
          employees: state.employees.map(employee => 
            employee.id === employeeId ? updatedEmployee : employee
          )
        });
        
        return updatedEmployee;
      } catch (error) {
        console.error('Error updating employee:', error);
        
        // Revert optimistic update
        set({ employees: originalEmployees });
        
        const errorInfo = ErrorHandler.handleApiError(error, 'Update Employee');
        set({ error: errorInfo.message });
        throw error;
      }
    },

    deleteEmployee: async (employeeId) => {
      const state = get();
      const originalEmployees = [...state.employees];
      
      // Optimistic update
      set({
        employees: state.employees.filter(employee => employee.id !== employeeId)
      });

      try {
        await dataService.deleteEmployee(employeeId);
        
        // Refresh dashboard counts (non-blocking)
        try {
          const updatedStats = await dataService.getDashboardCounts();
          if (updatedStats && typeof updatedStats === 'object') {
            set({ stats: {
              totalProjects: updatedStats.totalProjects || updatedStats.total_projects || 0,
              ongoingProjects: updatedStats.ongoingProjects || updatedStats.ongoing_projects || 0,
              completedProjects: updatedStats.completedProjects || updatedStats.completed_projects || 0,
              pendingContacts: updatedStats.pendingContacts || updatedStats.pending_contacts || 0,
              totalEmployees: updatedStats.totalEmployees || updatedStats.total_employees || 0,
              freeEmployees: updatedStats.freeEmployees || updatedStats.free_employees || 0
            }});
          } else {
            set({ stats: updatedStats });
          }
        } catch (statsError) {
          console.warn('Failed to refresh dashboard counts after employee deletion:', statsError);
          // Update employee count manually
          const currentState = get();
          set(state => ({
            stats: {
              ...state.stats,
              totalEmployees: currentState.employees.length,
              freeEmployees: currentState.freeEmployees.length
            }
          }));
        }
        
      } catch (error) {
        console.error('Error deleting employee:', error);
        
        // Revert optimistic update
        set({ employees: originalEmployees });
        
        const errorInfo = ErrorHandler.handleApiError(error, 'Delete Employee');
        set({ error: errorInfo.message });
        throw error;
      }
    },

    assignEmployeeToProject: async (employeeId, projectId) => {
      try {
        await dataService.assignEmployeeToProject(employeeId, projectId);
        
        // Refresh employees, projects, and ongoing projects list
        const [updatedEmployees, updatedProjects, updatedOngoingList] = await Promise.all([
          dataService.getAllEmployees(),
          dataService.getAllProjects(),
          dataService.getOngoingProjectsList()
        ]);
        
        set({ 
          employees: updatedEmployees,
          projects: updatedProjects,
          ongoingProjectsList: updatedOngoingList
        });
        
      } catch (error) {
        console.error('Error assigning employee to project:', error);
        const errorInfo = ErrorHandler.handleApiError(error, 'Assign Employee');
        set({ error: errorInfo.message });
        throw error;
      }
    },

    unassignEmployeeFromProject: async (employeeId, projectId) => {
      try {
        await dataService.unassignEmployeeFromProject(employeeId, projectId);
        
        // Refresh employees, projects, and ongoing projects list
        const [updatedEmployees, updatedProjects, updatedOngoingList] = await Promise.all([
          dataService.getAllEmployees(),
          dataService.getAllProjects(),
          dataService.getOngoingProjectsList()
        ]);
        
        set({ 
          employees: updatedEmployees,
          projects: updatedProjects,
          ongoingProjectsList: updatedOngoingList
        });
        
      } catch (error) {
        console.error('Error unassigning employee from project:', error);
        const errorInfo = ErrorHandler.handleApiError(error, 'Unassign Employee');
        set({ error: errorInfo.message });
        throw error;
      }
    },

    // Utility actions
    clearError: () => set({ error: null }),
    
    clearData: () => {
      dataService.clearCache();
      set({
        projects: [],
        ongoingProjectsList: [],
        contacts: [],
        clients: [],
        employees: [],
        freeEmployees: [],
        stats: {
          totalProjects: 0,
          ongoingProjects: 0,
          completedProjects: 0,
          pendingContacts: 0,
          totalEmployees: 0,
          freeEmployees: 0
        },
        isLoading: false,
        isRefreshing: false,
        lastRefresh: null,
        error: null,
        dataLoaded: false
      });
    }
  }))
);